export type FeedbackAction = 'like' | 'skip' | 'topic' | 'dislike' | 'save'

export interface PostItem {
  id: string
  platform?: string
  author?: string | null
  author_handle?: string | null
  url?: string | null
  text?: string | null
  media_desc?: string | null
  media_urls?: string[]
  posted_at?: string | null
  collected_at?: string | null
  bucket?: string
  tags?: string[]
  relevance?: number | null
  timeliness?: 'flash' | 'news' | 'actionable' | 'evergreen' | string | null
  hours_left?: number | null
  freshness?: number | null
  expired?: boolean
  overdue_kept?: boolean
  one_liner?: string | null
  reason?: string | null
  feedback?: string | null
  explore?: boolean
  source_kind?: 'external' | 'social' | string
  source_name?: string | null
  source_id?: string | null
}

export interface FeedState {
  mission: string
  mode: string
  cat: string
  pending_classify: number
  attention: Record<string, unknown>
  diversity?: Record<string, unknown>
  generated_at?: string
  since?: string
  inbox: PostItem[]
  reviewed: PostItem[]
  view_items: PostItem[]
  deck_items: PostItem[]
  throne_items: PostItem[]
  throne_pending: number
  empty_reason?: EmptyReason | null
  collect_warn?: Array<{
    platform?: string
    label?: string
    streak?: number
    note?: string | null
    health?: string | null
  }>
  hub: {
    inbox: {
      buckets: Record<string, PostItem[]>
      counts: Record<string, number>
      tags: Record<string, PostItem[]>
      tag_counts: Record<string, number>
    }
    all: {
      buckets: Record<string, PostItem[]>
      counts: Record<string, number>
      tags: Record<string, PostItem[]>
      tag_counts: Record<string, number>
    }
  }
  counts: Record<string, number>
  accounts: Account[]
  jobs: Record<string, unknown>
  connected_count: number
  login_needed?: string[]
  pipeline_last?: Record<string, unknown> | null
  bubble?: {
    level: string
    message: string
    top2_positive_share?: number
    top3_weight_share?: number
    top_tags?: string[]
  } | null
  actions: Record<string, { label: string; effect: string }>
}

export interface Account {
  platform: string
  label: string
  status: string
  connected: boolean
  last_check_at?: string | null
  last_collect_at?: string | null
  last_error?: string | null
  last_collect_count?: number
  profile_hint?: string | null
  note?: string | null
  zero_yield_streak?: number
  health?: string | null
}

export interface EmptyReason {
  code: string
  message: string
  inbox?: number
  surface?: number
  expired_pending?: number
  suppressed_hint?: number
}

/** API origin for cloud SPA → local compute. Empty = same origin. */
let _apiBase = ''
let _accessToken = ''
let _runtimeLoaded = false
let _staticMode = false

export function getApiBase(): string {
  return _apiBase.replace(/\/$/, '')
}

export function setApiBase(base: string) {
  _apiBase = (base || '').replace(/\/$/, '')
}

export function isStaticMode(): boolean {
  return _staticMode || !getApiBase()
}

export function getAccessToken(): string {
  if (_accessToken) return _accessToken
  try {
    return localStorage.getItem('digest_token') || ''
  } catch {
    return ''
  }
}

export function hasAccessToken(): boolean {
  return !!getAccessToken()
}

export function setAccessToken(tok: string) {
  _accessToken = tok || ''
  try {
    if (tok) localStorage.setItem('digest_token', tok)
    else localStorage.removeItem('digest_token')
  } catch {
    /* ignore */
  }
}

/** Typed client errors so UI can toast human messages. */
export class ApiError extends Error {
  code: string
  status?: number
  constructor(code: string, message: string, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

function tokHeaders(): Record<string, string> {
  const t = getAccessToken()
  return t ? { Authorization: `Bearer ${t}` } : {}
}

/** Load runtime-config.json (written by publish) + VITE_API_BASE. */
export async function loadRuntimeConfig(): Promise<void> {
  if (_runtimeLoaded) return
  _runtimeLoaded = true
  const viteBase = (import.meta as any).env?.VITE_API_BASE as string | undefined
  if (viteBase) setApiBase(viteBase)
  try {
    const r = await fetch('/runtime-config.json', { cache: 'no-store' })
    if (r.ok) {
      const j = await r.json()
      if (j?.apiBase) setApiBase(String(j.apiBase))
      // hybrid if we have a live API base
      if (getApiBase()) {
        _staticMode = false
      } else if (j?.mode === 'static' || !j?.apiBase) {
        _staticMode = true
      }
    } else {
      _staticMode = !getApiBase()
    }
  } catch {
    _staticMode = !getApiBase()
  }
  try {
    const u = new URL(location.href)
    const q = u.searchParams.get('api')
    if (q) {
      setApiBase(q)
      _staticMode = false
    }
    const tok = u.searchParams.get('token')
    if (tok) setAccessToken(tok)
  } catch {
    /* ignore */
  }
  // same-origin local serve → not static
  if (!getApiBase() && location.port === '8787') {
    _staticMode = false
  }
}

type ReqInit = RequestInit & { authRedirect?: boolean }

async function reqJson<T>(url: string, init?: ReqInit): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  }
  if (init?.method && init.method !== 'GET') {
    headers['Content-Type'] = 'application/json'
  }
  const tok = getAccessToken()
  if (tok && !headers.Authorization) headers.Authorization = `Bearer ${tok}`
  const base = getApiBase()
  const full = url.startsWith('http') ? url : `${base}${url}`
  const { authRedirect = true, ...fetchInit } = init || {}
  let res: Response
  try {
    res = await fetch(full, {
      credentials: base ? 'include' : 'same-origin',
      ...fetchInit,
      headers,
    })
  } catch {
    throw new ApiError(
      'network',
      base ? '連不上本機 API（需 Tailscale 且本機在線）' : '網路錯誤',
    )
  }
  if (res.status === 401 || (res.redirected && res.url.includes('/login'))) {
    // static snapshot site: never bounce to login for missing API
    if (_staticMode && !base) {
      throw new ApiError('static-readonly', '雲端只讀快照，無法寫入')
    }
    // drop stale token so next login is clean
    if (tok) setAccessToken('')
    if (authRedirect && !location.pathname.startsWith('/login')) {
      const next = encodeURIComponent(location.pathname + location.search)
      // defer so callers can toast before navigation
      window.setTimeout(() => {
        location.href = `/login?next=${next}`
      }, 350)
    }
    throw new ApiError('unauthorized', '請先登入存取碼', 401)
  }
  if (!res.ok) {
    const t = await res.text()
    let detail = t || res.statusText
    try {
      const j = JSON.parse(t)
      if (j?.detail) detail = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail)
      else if (j?.error) detail = String(j.error)
    } catch {
      /* plain text */
    }
    if (res.status === 404) throw new ApiError('not-found', detail || '找不到這則', 404)
    throw new ApiError('http', detail || `HTTP ${res.status}`, res.status)
  }
  const ct = (res.headers.get('content-type') || '').toLowerCase()
  // SPA fallback may return HTML 200 for missing API routes — treat as failure
  if (!ct.includes('application/json')) {
    const body = (await res.text()).slice(0, 80)
    throw new ApiError('not-json', `回應不是 JSON（${ct || 'unknown'}）: ${body}`)
  }
  return res.json()
}

async function loadStaticFeed(): Promise<FeedState> {
  const snap = await fetch('/api/feed.json', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  })
  if (!snap.ok) throw new Error(`static feed HTTP ${snap.status}`)
  const ct = (snap.headers.get('content-type') || '').toLowerCase()
  if (!ct.includes('json')) throw new Error('static feed is not JSON')
  const data = (await snap.json()) as FeedState
  ;(data as any).static_mode = true
  return data
}

export const api = {
  feed: async (q: { days?: number; mode?: string; cat?: string } = {}) => {
    // Live API when configured (hybrid cloud → Tailscale)
    if (getApiBase() || !_staticMode) {
      try {
        const p = new URLSearchParams()
        if (q.days != null) p.set('days', String(q.days))
        if (q.mode) p.set('mode', q.mode)
        if (q.cat) p.set('cat', q.cat)
        // feed 讀取失敗時可退快照；不要自動跳 login（寫入才跳）
        const live = await reqJson<FeedState>(`/api/feed?${p}`, { authRedirect: false })
        ;(live as any).static_mode = false
        ;(live as any).static_browse = false
        return live
      } catch (e: any) {
        const code = e instanceof ApiError ? e.code : ''
        if (code === 'static-readonly') throw e
        // 未登入 / 離線 / 5xx → 快照瀏覽（readOnly 擋寫入）
        try {
          const snap = await loadStaticFeed()
          ;(snap as any).static_browse = true
          ;(snap as any).static_mode = true
          return snap
        } catch {
          throw e
        }
      }
    }
    return loadStaticFeed()
  },
  feedback: (post_id: string, action: FeedbackAction, reading_ms?: number) =>
    reqJson<{ ok: boolean; action: string; reading_ms?: number }>('/api/feedback', {
      method: 'POST',
      body: JSON.stringify({
        post_id,
        action,
        ...(reading_ms != null ? { reading_ms } : {}),
      }),
    }),
  status: async () => {
    if (_staticMode && !getApiBase()) {
      try {
        return await reqJson<Record<string, unknown>>('/api/status.json')
      } catch {
        return { ok: true, static_mode: true }
      }
    }
    return reqJson<Record<string, unknown>>('/api/status')
  },
  insights: (days = 30) => reqJson<Record<string, unknown>>(`/api/insights?days=${days}`),
  pipelineStatus: () =>
    reqJson<{
      running: boolean
      error?: string | null
      last_run?: Record<string, unknown> | null
      automation?: Record<string, unknown>
    }>('/api/pipeline/status'),
  pipelineRun: () =>
    reqJson<{ ok: boolean; started?: boolean; error?: string }>('/api/pipeline/run', {
      method: 'POST',
    }),
  accounts: () => reqJson<{ accounts: Account[]; jobs: Record<string, unknown> }>('/api/accounts'),
  accountLogin: (platform: string) =>
    reqJson(`/api/accounts/${platform}/login?headed=true`, { method: 'POST' }),
  accountCheck: (platform: string) =>
    reqJson(`/api/accounts/${platform}/check`, { method: 'POST' }),
  accountCollect: (platform: string) =>
    reqJson(`/api/accounts/${platform}/collect?max_posts=40`, { method: 'POST' }),
  accountDisconnect: (platform: string, wipe = false) =>
    reqJson(`/api/accounts/${platform}/disconnect?wipe=${wipe ? 'true' : 'false'}`, {
      method: 'POST',
    }),
  collectAll: () => reqJson('/api/accounts/collect_all?max_posts=30', { method: 'POST' }),
  classify: () => reqJson<{ ok: boolean; classified?: number }>('/api/classify', { method: 'POST' }),
  ingest: (body: { text: string; platform: string; author?: string; url?: string }) =>
    reqJson('/api/ingest', { method: 'POST', body: JSON.stringify({ ...body, classify: true }) }),
  login: async (token: string) => {
    const base = getApiBase()
    try {
      localStorage.setItem('digest_token', token)
    } catch {
      /* ignore */
    }
    setAccessToken(token)
    // Validate against live API (local or hybrid apiBase)
    try {
      const s = await fetch(`${base}/api/status`, {
        credentials: base ? 'include' : 'same-origin',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      if (s.ok) {
        const ct = (s.headers.get('content-type') || '').toLowerCase()
        if (ct.includes('json')) return true
      }
      if (s.status === 401) return false
    } catch {
      /* try form login on same host */
    }
    if (!base && _staticMode) {
      // pure static host with no API — cannot really auth
      return false
    }
    try {
      const body = new URLSearchParams({ token, next: '/' })
      const res = await fetch(`${base}/login`, {
        method: 'POST',
        credentials: base ? 'include' : 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
        redirect: 'follow',
      })
      const s = await fetch(`${base}/api/status`, {
        credentials: base ? 'include' : 'same-origin',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      return s.ok
    } catch {
      return false
    }
  },
  health: () => reqJson<{ ok: boolean }>('/health'),
}
